import type { AIEndpointProfile } from '../types';

export const providerDefaults: Record<AIEndpointProfile['provider'], Partial<AIEndpointProfile>> = {
  openai: { name: 'OpenAI Compatible', url: 'https://api.openai.com/v1/images/edits', authHeader: 'Authorization', responsePath: 'data.0.b64_json' },
  gemini: { name: 'Google Gemini', url: 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-exp:generateContent', authHeader: 'x-goog-api-key', responsePath: 'candidates.0.content.parts.0.inlineData.data' },
  stability: { name: 'Stability AI', url: 'https://api.stability.ai/v2beta/stable-image/edit/inpaint', authHeader: 'Authorization', responsePath: 'image' },
  replicate: { name: 'Replicate', url: 'https://api.replicate.com/v1/predictions', authHeader: 'Authorization', responsePath: 'output.0' },
  fal: { name: 'Fal', url: 'https://fal.run/fal-ai/flux/dev/image-to-image', authHeader: 'Authorization', responsePath: 'images.0.url' },
  custom: { name: 'Custom HTTP', url: '', authHeader: 'Authorization', responsePath: 'data.0.url' }
};

export function readPath(value: unknown, path: string): unknown {
  return path.split('.').filter(Boolean).reduce<unknown>((current, key) => {
    if (current == null) return undefined;
    return (current as Record<string, unknown>)[/^\d+$/.test(key) ? Number(key) : key];
  }, value);
}

function interpolateTemplate(template: string, variables: Record<string, string>) {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => variables[key] ?? '');
}

export async function runAI(profile: AIEndpointProfile, imageDataUrl: string, prompt: string): Promise<string> {
  const defaultBody = { prompt, image: imageDataUrl, response_format: 'b64_json' };
  let body: unknown = defaultBody;
  if (profile.requestTemplate.trim()) {
    const rendered = interpolateTemplate(profile.requestTemplate, { prompt, image: imageDataUrl });
    body = JSON.parse(rendered);
  }
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (profile.apiKey) headers[profile.authHeader || 'Authorization'] = profile.authHeader.toLowerCase() === 'authorization' && !profile.apiKey.startsWith('Bearer ') ? `Bearer ${profile.apiKey}` : profile.apiKey;

  const response = profile.transport === 'proxy'
    ? await fetch(`${profile.proxyUrl.replace(/\/$/, '')}/api/ai/request`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ url: profile.url, method: profile.method, headers, body }) })
    : await fetch(profile.url, { method: profile.method, headers, body: JSON.stringify(body) });
  if (!response.ok) throw new Error(`${response.status} ${await response.text()}`);
  const type = response.headers.get('content-type') || '';
  if (type.startsWith('image/')) return URL.createObjectURL(await response.blob());
  const json = await response.json();
  const result = readPath(json, profile.responsePath);
  if (typeof result !== 'string') throw new Error('响应中没有找到图片，请检查响应路径');
  if (result.startsWith('http') || result.startsWith('data:')) return result;
  return `data:image/png;base64,${result}`;
}
